import type { MetricKey, RangeReading } from './types';

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

export interface BucketedPoint {
  t: number; // bucket start (epoch ms)
  value: number; // mean of the readings in the bucket
  count: number; // how many readings were averaged
}

// Bucket width for a given range span. A device reporting every 15 minutes puts
// 672 points in a 7-day window, which renders as a solid band rather than a
// legible line, so the chart averages each bucket into one point instead:
// 24h → 1h (24 points), 7d → 4h (42), 30d → 12h (60).
//
// Breakpoints are deliberately loose (1.5d / 10d / 45d) rather than exact.
// computeRange() returns a span one RANGE_BUCKET_MS longer than the nominal
// preset — `to` is the END of the current minute bucket — so the "24h" preset
// is really 24h+1min, and an exact `<= DAY_MS` test would demote every preset
// into the next tier up. The slack also gives arbitrary custom ranges a
// sensible width instead of needing a case of their own.
export function bucketSizeMs(spanMs: number): number {
  if (spanMs <= DAY_MS * 1.5) return HOUR_MS;
  if (spanMs <= DAY_MS * 10) return 4 * HOUR_MS;
  if (spanMs <= DAY_MS * 45) return 12 * HOUR_MS;
  return DAY_MS;
}

// Averages readings into fixed-width buckets, oldest first.
//
// Buckets are epoch-aligned (not anchored to the range start) so boundaries
// stay put between refetches — the chart polls on an interval, and a drifting
// origin would reshuffle every point and make the line visibly jitter. Because
// 1h/4h/12h/24h all divide evenly into a day, the buckets also land on clean
// UTC wall-clock times.
//
// A bucket containing no numeric sample is omitted rather than emitted with a
// null value: that preserves the chart's existing behaviour of drawing straight
// through a data outage (it sets connectNulls) instead of changing how gaps
// look as a side effect of bucketing.
export function bucketReadings(
  readings: RangeReading[],
  metric: MetricKey,
  bucketMs: number,
): BucketedPoint[] {
  const buckets = new Map<number, { sum: number; count: number }>();

  for (const r of readings) {
    const value = r[metric];
    // qAnalytics is nullable, and a metric that wasn't requested is absent.
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;

    const t = new Date(r.recordedAt).getTime();
    if (!Number.isFinite(t)) continue;

    const key = Math.floor(t / bucketMs) * bucketMs;
    const acc = buckets.get(key);
    if (acc) {
      acc.sum += value;
      acc.count += 1;
    } else {
      buckets.set(key, { sum: value, count: 1 });
    }
  }

  // The API returns rows oldest-first, but sort explicitly rather than relying
  // on it — the line is drawn in array order, so a stray ordering change
  // upstream would zigzag the chart rather than fail a test.
  return [...buckets.entries()]
    .sort(([a], [b]) => a - b)
    .map(([t, { sum, count }]) => ({ t, value: sum / count, count }));
}
